// @ts-strict-ignore
import { ArticleCompositionVO, ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleContainer } from './article-container';
import { Dictionary } from 'typescript-collections';
import { ProjectModel } from '@project/model/project.model';
import { ArticleContainerUtils } from './util/article-container-utils';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { ArticleRefreshData } from '@article/service/model/article-refresh-data';
import { ArticleUtils } from '@article/util/article-utils';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { CompositeModelDataHolder } from '@3d-scene/model3d/cache/model-data';
import { ArticleInfoVO, ProjectVO } from '@project/model/vo/project.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { CompositeArticleInformation } from '@article/model/composite-article-information';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleContainerFactory } from './article-container.factory';
import { Log } from '@shared/debug/log';
import { ProjectArticleService } from '@project/service/project-article.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ArticleCompositionUtils } from '@project/util/article-composition-utils';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { ProjectType } from '@project/model/project-type';
import { StringUtils } from '@shared/util/string-utils';

export class ArticleContainersWrapper {
  private _unusedArticleTypes: string[];

  private _wrapperGUID: string;
  private _groupId: string;
  private _searchVectorId: number;
  private _givingConnectionNames: string[];
  private _isArticleOnRail: boolean = false;

  private _article: ArticleVO;
  private _articleContainers: ArticleContainer[] = [];
  private _baseArticleContainer: ArticleContainer;
  private _baseArticleContainers: ArticleContainer[];
  private _articleUsageID: string;

  private _properties: Dictionary<string, any> = new Dictionary<string, any>();

  private _compositeArticleComposition: ArticleCompositionVO;

  private _processedProject: ProjectVO;

  private _stripComplexArticleRequired: boolean = false;

  constructor(
    article: ArticleVO,
    groupId: string,
    unusedTypes: string[],
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private articleContainerLoader: ArticleContainerLoader,
    private compositeArticleCacheManager: CompositeArticleCacheManager,
    private articleContainerFactory: ArticleContainerFactory,
    private articleManager: ArticleManager,
    private articleContainerUtils: ArticleContainerUtils,
    private detachArticleUsageService: DetachArticleUsageService
  ) {
    this._wrapperGUID = StringUtils.createUuid();
    this._groupId = groupId;
    this._article = article;
    this._unusedArticleTypes = !unusedTypes ? [] : unusedTypes;
  }

  public get wrapperGUID(): string {
    return this._wrapperGUID;
  }

  public get article(): ArticleVO {
    return this._article;
  }

  public get baseArticle(): ArticleVO {
    return !this._article.baseArticle ? this._article : this._article.baseArticle;
  }

  public get baseArticleContainer(): ArticleContainer {
    if (!this._baseArticleContainer) {
      this._baseArticleContainer = this.findFirstBaseContainer();
    }
    return this._baseArticleContainer;
  }

  public get baseArticleContainers(): ArticleContainer[] {
    if (!this._baseArticleContainers) {
      this._baseArticleContainers = ArticleContainerUtils.getBaseArticleContainers(this._articleContainers);
    }
    return this._baseArticleContainers;
  }

  public get articleContainers(): ArticleContainer[] {
    return this._articleContainers;
  }

  public get properties(): Dictionary<string, any> {
    return this._properties;
  }

  public get articleUsageID(): string {
    return this._articleUsageID;
  }

  public set articleUsageID(value: string) {
    this._articleUsageID = value;
  }

  public get searchVectorId(): number {
    return this._searchVectorId;
  }

  public set searchVectorId(vectorId: number) {
    this._searchVectorId = vectorId;
  }

  public get givingConnectionNames(): string[] {
    return this._givingConnectionNames;
  }

  public set givingConnectionNames(connectionNames: string[]) {
    this._givingConnectionNames = connectionNames;
  }

  public get isArticleOnRail(): boolean {
    return this._isArticleOnRail;
  }

  public set isArticleOnRail(isArticleOnRail: boolean) {
    this._isArticleOnRail = isArticleOnRail;
  }

  public async refreshWrapper(storeArticleUsage: boolean = true): Promise<void> {
    this._articleContainers = [];
    // TODO: (from flex) maybe add check if wrapper has already been created
    if (ArticleUtils.isCompositeArticle(this._article)) {
      try {
        const compositeModelDataHolder = await this.compositeArticleCacheManager.loadCompositeArticle(this._article)
        await this.parseModel(compositeModelDataHolder);
      } catch (error) {
        Log.error('Could not load composit article: ', error);
        this.connectArticle_failure()
      }
    } else {
      await this.createSingleArticleContainer(this._article, this._groupId, storeArticleUsage);
    }
  }

  private findFirstBaseContainer(): ArticleContainer {
    let firstBaseContainer: ArticleContainer = this.findFirstBaseContainerByArticle(this.baseArticle);
    if (!firstBaseContainer) {
      firstBaseContainer = this.baseArticleContainers.length > 0 ? this.baseArticleContainers[0] : null;
    }
    return firstBaseContainer;
  }

  private findFirstBaseContainerByArticle(article: ArticleVO): ArticleContainer {
    if (!article) {
      return null;
    }
    for (const container of this._articleContainers) {
      const articleUsage: ArticleUsageVO = container.articleUsage;
      if (articleUsage && article.identity === articleUsage.articleId && article.version === articleUsage.articleVersion) {
        return container;
      }
    }
    return null;
  }

  private async refreshArticleContainers(refreshData: ArticleRefreshData): Promise<void> {
    if (this.baseArticleContainer) {
      this.baseArticleContainer.properties = this._properties;
    }
    // exclude wrappers without article usages (added to handle invalid cases when replacing labels)
    const containersWithUsage: ArticleContainer[] = this._articleContainers.filter((container) => !!container.articleUsage);
    if (ArrayUtils.isEmpty(containersWithUsage)) {
      throw new Error(this.wrapperGUID);
    }
    try {
      await this.articleContainerLoader.refreshContainers(containersWithUsage, refreshData);
    } catch (error) {
      Log.error('Could not refresh container: ', error);
      await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(ArticleContainerUtils.extractArticleGUIDs(containersWithUsage));
      throw new Error(this.wrapperGUID);
    }
  }

  private createSingleArticleContainer(article: ArticleVO, groupId: string = null, storeArticleUsage: boolean): Promise<void> {
    let articleUsage: ArticleUsageVO;
    let articleGUID: string;

    if (this._articleUsageID) {
      articleGUID = this._articleUsageID;
    } else {
      articleUsage = storeArticleUsage
        ? this.projectArticleService.createNewArticleUsage(article.identity, article.version, groupId)
        : this.projectArticleService.createUnstoredNewArticleUsage(article.identity, article.version, groupId);
      articleGUID = articleUsage.id;
    }
    const articleContainer: ArticleContainer = this.createArticleContainer(articleGUID);
    if (articleUsage) {
      articleContainer.updateArticleUsage(articleUsage);
    }
    const refreshData: ArticleRefreshData = new ArticleRefreshData();
    refreshData.deleteUnusedConnections = false;
    return this.refreshArticleContainers(refreshData);
  }

  private createArticleCompositionToLoad(): void {
    if (!this._compositeArticleComposition) {
      return;
    }
    for (const articleUsageId of ArticleCompositionUtils.articleUsageIds(this._compositeArticleComposition)) {
      this.createArticleContainer(articleUsageId);
    }
  }

  private createCompositeArticleComponents(refreshData: ArticleRefreshData): Promise<void> {
    this.createArticleCompositionToLoad();

    const compositeGroupId: string = StringUtils.createUuid();
    let compositePartSelectionAllowed: boolean = true;
    if (PropertyUtils.containsProperty(this.article.behaviorProperties, ArticleBehaviorPropertyDef.IS_COMPOSITE_PART_SELECTION_ALLOWED)) {
      compositePartSelectionAllowed = PropertyUtils.getBooleanPropertyValue(
        this.article.behaviorProperties,
        ArticleBehaviorPropertyDef.IS_COMPOSITE_PART_SELECTION_ALLOWED
      );
    }

    const processedUsageGroupIds: string[] = []; // to handle usages in which groupId has already been replaced
    const clonedArticleUsages: ArticleUsageVO[] = ArticleUsageUtils.cloneArticleUsages(this._compositeArticleComposition.articleUsages);
    const oldUsageIdToNewId: Dictionary<string, string> = new Dictionary<string, string>();
    for (const clonedArticleUsage of clonedArticleUsages) {
      const container: ArticleContainer = this.articleContainerUtils.findArticleContainerByGUID(clonedArticleUsage.id, this._articleContainers);
      if (container) {
        // the following property is used by CompositeArticleManager
        const compositePartUsageId: string = clonedArticleUsage.id;
        const newUsageId: string = StringUtils.createUuid();

        oldUsageIdToNewId.setValue(clonedArticleUsage.id, newUsageId);
        refreshData.updateArticleInfo(clonedArticleUsage.id, newUsageId);

        ArticleUsageUtils.replaceArticleUsageId(clonedArticleUsages, clonedArticleUsage.id, newUsageId);
        clonedArticleUsage.id = newUsageId;
        container.articleGUID = clonedArticleUsage.id;
        ReferenceArticleUsageUtils.removeAllReferences(clonedArticleUsage);

        const compositeArticleInfo: CompositeArticleInformation = new CompositeArticleInformation(
          this._wrapperGUID,
          this._article.identity,
          this._article.version,
          this._article.articleNo,
          compositePartUsageId
        );
        if (this._stripComplexArticleRequired) {
          CompositeArticleUtils.applyStrippedCompositeArticleInformation(clonedArticleUsage, compositeArticleInfo);
        } else {
          CompositeArticleUtils.applyCompositeArticleInformation(clonedArticleUsage, compositeArticleInfo);
        }

        if (!compositePartSelectionAllowed && !this._stripComplexArticleRequired) {
          clonedArticleUsage.groupId = compositeGroupId;
        } else {
          if (clonedArticleUsage.groupId && processedUsageGroupIds.indexOf(clonedArticleUsage.groupId) === -1) {
            const newGroupId: string = StringUtils.createUuid();
            ArticleUsageUtils.replaceArticleUsageGroupId(clonedArticleUsages, clonedArticleUsage.groupId, newGroupId);
            processedUsageGroupIds.push(newGroupId);
          }
        }
        this.projectArticleService.storeArticleUsage(clonedArticleUsage);
      }
    }

    this.initializeDynamicArticles(refreshData, oldUsageIdToNewId);

    // check if we use usages for getting geometric data
    if (!ProjectUtils.isProjectRequiresView3DRepresentation(this._processedProject)) {
      this.updateCompositeArticles();
    }
    return this.refreshArticleContainers(refreshData);
  }

  private initializeDynamicArticles(refreshData: ArticleRefreshData, oldUsageIdToNewId: Dictionary<string, string>): void {
    if (!refreshData.dynamicArticlesMap) {
      return;
    }

    for (const key of refreshData.dynamicArticlesMap.keys()) {
      const dynamicContainer: ArticleContainer = this.articleContainerUtils.findArticleContainerByGUID(key, this._articleContainers);
      if (dynamicContainer) {
        const info: ArticleInfoVO = ArticleUtils.findArticleInfoByGuidInSource(key, refreshData.dynamicArticlesInfo);
        if (info) {
          for (const attribute of info.attributes) {
            if (ArticleContainerPropertyDef.ARTICLES_GUIDS_BETWEEN_ADAPTERS === attribute.name) {
              const newUsageIdsBetweenAdapters: string[] = this.getNewUsageIdsBetweenAdapters(oldUsageIdToNewId, attribute.values);
              attribute.values = newUsageIdsBetweenAdapters;
            }
          }
        }
      }
    }
  }

  private getNewUsageIdsBetweenAdapters(oldUsageIdToNewId: Dictionary<string, string>, oldUsageIds: string[]): string[] {
    if (!oldUsageIds || oldUsageIds.length === 0) {
      return [];
    }
    const result: string[] = [];
    for (const oldUsageId of oldUsageIds) {
      if (oldUsageIdToNewId.containsKey(oldUsageId)) {
        result.push(oldUsageIdToNewId.getValue(oldUsageId));
      }
    }
    return result;
  }

  private updateCompositeArticles(): void {
    for (const container of this._articleContainers) {
      ArticleContainerUtils.applyTransformationToContainerByUsageProperties(container);
    }
  }

  private createArticleContainer(articleUsageId: string): ArticleContainer {
    const articleContainer: ArticleContainer = this.articleContainerFactory.getNewInstance();
    articleContainer.articleGUID = articleUsageId;
    this._articleContainers.push(articleContainer);
    return articleContainer;
  }

  private async parseModel(compositeModelDataHolder: CompositeModelDataHolder): Promise<void> {
    const articleVO: ArticleVO = compositeModelDataHolder.article;
    if (this._processedProject || articleVO.identity !== this._article.identity || articleVO.version !== this._article.version) {
      return;
    }

    this._processedProject = compositeModelDataHolder.complexProject.project;

    const refreshData: ArticleRefreshData = ArticleRefreshData.createFromProject(compositeModelDataHolder.complexProject);
    refreshData.delayDynamicArticleFinalization = true; // dynamic connections should be created only when article attached to project
    refreshData.deleteUnusedConnections = false;
    const projectCompositions: ArticleCompositionVO[] = this._processedProject.compositions;
    if (projectCompositions && projectCompositions.length > 0) {
      this._compositeArticleComposition = projectCompositions[0];
    }

    if (this._compositeArticleComposition) {
      const articles: ArticleVO[] = compositeModelDataHolder.complexProject.articles;

      await this.articleManager.addArticlesToCache(articles);
      this.removeUnusedArticlesInComposition(this._compositeArticleComposition, articles);

      this._stripComplexArticleRequired = this.strippingComplexArticleInformationRequired(
        this.projectModel.currentProjectType, this._compositeArticleComposition.articleUsages);
      return this.createCompositeArticleComponents(refreshData);
    }

    this.connectArticle_failure();
    throw new Error('Could not load composite article');
  }

  private removeUnusedArticlesInComposition(composition: ArticleCompositionVO, articles: ArticleVO[]): void {
    for (const articleUsage of composition.articleUsages.concat()) {
      if (this.isCompositePartExcluded(articleUsage)) {
        ArticleCompositionUtils.removeUnusedArticleUsageFromComposition(composition, articleUsage);
      }
    }
    for (const article of articles) {
      if (this.isArticleUnused(article)) {
        ArticleCompositionUtils.removeUnusedArticleUsageFromCompositionByIdAndVersion(composition, article.identity, article.version);
      }
    }
  }

  private isCompositePartExcluded(articleUsage: ArticleUsageVO): boolean {
    return ArticleUsageUtils.getArticleUsageInfoPropertyBooleanValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_PART_EXCLUDED);
  }

  private strippingComplexArticleInformationRequired(currentProjectType: ProjectType, articleUsages: ArticleUsageVO[]): boolean {
    if (!this.isArticleOnRail) {
      return false;
    }
    const strippingRequired: boolean = ArticleUsageUtils.isStrippingComplexArticleInformationRequired(currentProjectType, articleUsages);
    return strippingRequired;
  }

  private connectArticle_failure(): void {
    if (this._baseArticleContainer) {
      Object3DUtils.dispose(this._baseArticleContainer);
    }
    for (const articleContainer of this._articleContainers) {
      Object3DUtils.dispose(articleContainer);
    }
  }

  private isArticleUnused(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return this._unusedArticleTypes.indexOf(article.type.displayName) > -1;
  }

  public disposeArticleContainers() {
    for (const articleContainer of this._articleContainers) {
      Object3DUtils.dispose(articleContainer);
    }
  }
}
